import React, { useMemo } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;
const SHIFT_CYCLE = ['A Shift', 'A Shift', 'B Shift', 'B Shift', 'C Shift', 'C Shift'];

/*
 * Metro suppression uses a repeating 48/96 A,A,B,B,C,C cycle.
 * 2026-08-15 and 2026-08-16 are the verified A-shift pair in the roster data,
 * so 2026-08-15 is a stable cycle anchor for date-only calculations.
 */
const SHIFT_ANCHOR_UTC = Date.UTC(2026, 7, 15);

const ENGINE_DEMAND = {
  FF: 2,
  ENG: 1,
  CAP: 1,
};

function dateUtc(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function shiftForDate(value) {
  const utc = dateUtc(value);
  if (utc === null) return '';
  const delta = Math.round((utc - SHIFT_ANCHOR_UTC) / DAY_MS);
  const index = ((delta % SHIFT_CYCLE.length) + SHIFT_CYCLE.length) % SHIFT_CYCLE.length;
  return SHIFT_CYCLE[index];
}

function formatDate(value) {
  const utc = dateUtc(value);
  if (utc === null) return value || '—';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(utc));
}

function parseAvailability(value) {
  const text = String(value ?? '').trim();
  if (!text || /n\/a|—|--/i.test(text)) return null;

  const full = text.match(/(\d+)\s*(?:Full|F)\b/i);
  const partial = text.match(/(\d+)\s*(?:Partial|P)\b/i);

  if (full) {
    return {
      full: Number(full[1]),
      partial: partial ? Number(partial[1]) : 0,
    };
  }

  const n = Number(text);
  return Number.isFinite(n) ? { full: n, partial: 0 } : null;
}

function numberValue(value) {
  const n = Number(String(value ?? '').trim());
  return Number.isFinite(n) ? n : 0;
}

function rankImpact(row, prefix, demand, affected) {
  const available = parseAvailability(row?.[`${prefix} Avail`]);
  const fullBefore = available?.full ?? null;
  const partial = available?.partial ?? 0;
  const fullAfter = affected && fullBefore !== null
    ? Math.max(0, fullBefore - demand)
    : fullBefore;
  const shortfall = affected && fullBefore !== null
    ? Math.max(0, demand - fullBefore)
    : 0;

  return {
    prefix,
    demand,
    fullBefore,
    partial,
    fullAfter,
    shortfall,
    vacancies: numberValue(row?.[`${prefix} Vac`]),
    mcb: numberValue(row?.[`${prefix} MCB`]),
  };
}

function rowOutlook(impacts, affected) {
  if (!affected) {
    return { tone: 'neutral', label: 'No shift impact' };
  }

  if (impacts.some(item => item.fullBefore === null)) {
    return { tone: 'neutral', label: 'List data unavailable' };
  }

  if (impacts.some(item => item.shortfall > 0)) {
    return { tone: 'bad', label: 'Insufficient full-day backfill' };
  }

  if (impacts.some(item => item.vacancies > 0)) {
    return { tone: 'bad', label: 'Current vacancy + engine demand' };
  }

  if (
    impacts.some(item => item.mcb > 0) ||
    impacts.some(item => item.fullAfter !== null && item.fullAfter <= 3)
  ) {
    return { tone: 'warn', label: 'Covered · limited reserve' };
  }

  return { tone: 'good', label: 'Backfill available' };
}

function toneRank(tone) {
  if (tone === 'bad') return 3;
  if (tone === 'warn') return 2;
  if (tone === 'good') return 1;
  return 0;
}

function poolTone(impact, affected) {
  if (!affected || impact.fullBefore === null) return 'pub-neutral';
  if (impact.shortfall > 0) return 'pub-bad';
  if (impact.fullAfter <= 3) return 'pub-warn';
  return 'pub-good';
}

function PoolCell({ impact, affected }) {
  if (!affected) return <span className="pub-engine-no-impact-text">—</span>;
  if (impact.fullBefore === null) return <span>—</span>;

  return (
    <div className="pub-engine-pool">
      <strong>{impact.fullBefore} → {impact.fullAfter}</strong>
      {impact.shortfall > 0 && <small>{impact.shortfall} uncovered</small>}
      {impact.partial > 0 && <small>+ {impact.partial} partial</small>}
    </div>
  );
}

function existingStress(impacts, affected) {
  if (!affected) return '—';
  const parts = [];
  impacts.forEach(item => {
    if (item.vacancies > 0) parts.push(`${item.prefix} ${item.vacancies} Vac`);
    if (item.mcb > 0) parts.push(`${item.prefix} ${item.mcb} MCB`);
  });
  return parts.length ? parts.join(' · ') : 'None';
}

export default function EngineImpact({ general, shift, onShiftChange }) {
  const rows = useMemo(() => {
    return (general?.rows || []).map(row => {
      const rowShift = shiftForDate(row.Date);
      const affected = Boolean(shift && rowShift === shift);
      const impacts = [
        rankImpact(row, 'FF', ENGINE_DEMAND.FF, affected),
        rankImpact(row, 'ENG', ENGINE_DEMAND.ENG, affected),
        rankImpact(row, 'CAP', ENGINE_DEMAND.CAP, affected),
      ];
      const outlook = rowOutlook(impacts, affected);
      return { row, rowShift, affected, impacts, outlook };
    });
  }, [general, shift]);

  const affectedRows = rows.filter(item => item.affected);
  const worst = affectedRows.reduce(
    (current, item) => toneRank(item.outlook.tone) > toneRank(current.tone) ? item.outlook : current,
    { tone: 'neutral', label: 'Select a shift' }
  );

  const summaryLabel = shift
    ? `${shift} engine company`
    : 'Choose A, B, or C Shift';

  return (
    <div className="pub-engine-impact">
      <div className="pub-engine-shift-picker" role="group" aria-label="Engine company shift">
        {['A Shift', 'B Shift', 'C Shift'].map(option => (
          <button
            type="button"
            key={option}
            className={`pub-engine-shift-btn${shift === option ? ' is-active' : ''}`}
            aria-pressed={shift === option}
            onClick={() => onShiftChange(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <div className="pub-note pub-engine-method">
        <strong>Engine Company model:</strong> 1 Captain + 1 Engineer + 2 Firefighters. The model is shift-and-rank only; no individual personnel are evaluated. Only the selected shift's normal 48/96 work days are affected. Full-day candidates are used to estimate 24-hour backfill. Day-1 partial candidates remain visible but do not count as complete engine-company coverage.
      </div>

      <div className="pub-summary pub-engine-summary">
        <div className="pub-box">
          <span>Resource</span>
          <strong className="shift">{summaryLabel}</strong>
        </div>
        <div className="pub-box">
          <span>Crew Model</span>
          <strong className="shift">1 CAP · 1 ENG · 2 FF</strong>
        </div>
        <div className="pub-box">
          <span>Affected Days</span>
          <strong>{shift ? `${affectedRows.length} / ${rows.length}` : '—'}</strong>
        </div>
        <div className="pub-box">
          <span>Worst 7-Day Outlook</span>
          <strong className={`shift pub-engine-summary-${worst.tone}`}>{shift ? worst.label : '—'}</strong>
        </div>
      </div>

      {!shift ? (
        <div className="pub-empty">Choose A, B, or C Shift to model releasing one engine company for deployment.</div>
      ) : (
        <div className="pub-table-wrap">
          <table className="pub-table pub-engine-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platoon</th>
                <th>FF Full Avail<br/><small>2 needed</small></th>
                <th>ENG Full Avail<br/><small>1 needed</small></th>
                <th>CAP Full Avail<br/><small>1 needed</small></th>
                <th>Existing Stress</th>
                <th>Engine Outlook</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(item => {
                const [ff, eng, cap] = item.impacts;
                return (
                  <tr key={item.row.Date} className={item.affected ? 'pub-engine-affected' : 'pub-engine-no-impact'}>
                    <td>{formatDate(item.row.Date)}</td>
                    <td>{item.rowShift || '—'}</td>
                    <td className={poolTone(ff, item.affected)}><PoolCell impact={ff} affected={item.affected}/></td>
                    <td className={poolTone(eng, item.affected)}><PoolCell impact={eng} affected={item.affected}/></td>
                    <td className={poolTone(cap, item.affected)}><PoolCell impact={cap} affected={item.affected}/></td>
                    <td className={item.affected ? '' : 'pub-neutral'}>{existingStress(item.impacts, item.affected)}</td>
                    <td className={`pub-${item.outlook.tone}`}>{item.outlook.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
